<script lang="ts" setup>
  import logger from '@leaflink/snitch';
  import { computed } from 'vue';

  import DirectPayLockup from './../../../assets/logos/direct-pay-lockup.svg';
  import FlexPayLockup from './../../../assets/logos/flex-pay-lockup.svg';
  import LeafLinkBrandmark from './../../../assets/logos/leaflink-brandmark.svg';
  import LeafLinkLockup from './../../../assets/logos/leaflink-lockup.svg';
  import MarketplaceBrandmark from './../../../assets/logos/marketplace-brandmark.svg';
  import PaymentsBrandmark from './../../../assets/logos/payments-brandmark.svg';
  import PaymentsLockup from './../../../assets/logos/payments-lockup.svg';
  import PostLockup from './../../../assets/logos/post-lockup.svg';

  export interface LogoProps {
    /**
     * brand to use for the logo. This, along with variant, will determine which logo to display.
     */
    brand?: 'leaflink' | 'payments' | 'marketplace' | 'flex-pay' | 'direct-pay' | 'post';

    /**
     * What color the logo should be.
     */
    color?: 'purple' | 'white';

    /**
     * Which variant of the logo to display.
     */
    variant?: 'lockup' | 'brandmark';

    /**
     * Width of the logo.
     */
    width?: string | number;

    /**
     * Height of the logo.
     */
    height?: string | number;
  }

  const props = withDefaults(defineProps<LogoProps>(), {
    brand: 'leaflink',
    color: 'purple',
    variant: 'lockup',
    width: undefined,
    height: undefined,
  });

  const emit = defineEmits<{
    /**
     * Fires when an invalid brand and variant combination is provided.
     */
    (e: 'error', msg: string): void;
  }>();

  const DEFAULT_LOGO_ATTRS = {
    LeafLinkLockup: {
      is: LeafLinkLockup,
      width: 139,
      height: 24,
    },
    PaymentsLockup: {
      is: PaymentsLockup,
      width: 164,
      height: 24,
    },
    LeafLinkBrandmark: {
      is: LeafLinkBrandmark,
      width: 29,
      height: 24,
    },
    MarketplaceBrandmark: {
      is: MarketplaceBrandmark,
      width: 24,
      height: 24,
    },
    PaymentsBrandmark: {
      is: PaymentsBrandmark,
      width: 24,
      height: 24,
    },
    DirectPayLockup: {
      is: DirectPayLockup,
      width: 107,
      height: 24,
    },
    FlexPayLockup: {
      is: FlexPayLockup,
      width: 89,
      height: 24,
    },
    PostLockup: {
      is: PostLockup,
      width: 60,
      height: 24,
    },
  };

  // Determine which logo to display based on brand and variant & set default sizes for convenience unless explicitly set.
  // Note: Setting default width or height on the SVG element itself will not allow the SVG to scale up above those values.
  const computedLogoAttrs = computed<{ is: string; width: number; height: number }>(() => {
    let attrs;

    if (props.brand === 'leaflink' && props.variant === 'lockup') {
      attrs = DEFAULT_LOGO_ATTRS['LeafLinkLockup'];
    }

    if (props.brand === 'leaflink' && props.variant === 'brandmark') {
      attrs = DEFAULT_LOGO_ATTRS['LeafLinkBrandmark'];
    }

    if (props.brand === 'marketplace' && props.variant === 'brandmark') {
      attrs = DEFAULT_LOGO_ATTRS['MarketplaceBrandmark'];
    }

    if (props.brand === 'payments' && props.variant === 'lockup') {
      attrs = DEFAULT_LOGO_ATTRS['PaymentsLockup'];
    }

    if (props.brand === 'direct-pay' && props.variant === 'lockup') {
      attrs = DEFAULT_LOGO_ATTRS['DirectPayLockup'];
    }

    if (props.brand === 'flex-pay' && props.variant === 'lockup') {
      attrs = DEFAULT_LOGO_ATTRS['FlexPayLockup'];
    }

    if (props.brand === 'post' && props.variant === 'lockup') {
      attrs = DEFAULT_LOGO_ATTRS['PostLockup'];
    }

    if (['payments', 'direct-pay', 'flex-pay', 'post'].includes(props.brand) && props.variant === 'brandmark') {
      attrs = DEFAULT_LOGO_ATTRS['PaymentsBrandmark'];
    }

    if (!attrs) {
      const msg = `Invalid brand and variant combination. There's no ${props.brand.replace(
        '-',
        ' ',
      )} ${props.variant.replace('-', ' ')} logo.`;
      logger.error(msg);
      emit('error', msg);

      return;
    }

    // If width or height is explicitly set, override the default values but do it individually.
    // SVG's will always maintain their aspect ratio, so setting one will scale the other accordingly, so if a user
    // sends in even ONE of these props, we want to make sure we delete both defaults so the SVG can scale in the
    // expected way.
    if (props.width || props.height) {
      delete attrs.width;
      delete attrs.height;
      if (props.width) {
        attrs.width = props.width;
      }
      if (props.height) {
        attrs.height = props.height;
      }
    }

    return attrs;
  });
</script>

<template>
  <component
    :is="computedLogoAttrs.is"
    v-if="computedLogoAttrs"
    class="stash-logo"
    :class="[
      `stash-logo--${props.brand}`,
      `stash-logo--${props.variant}`,
      `stash-logo--${props.color}`,
      {
        'text-purple-500': props.color === 'purple',
        'text-white': props.color === 'white',
      },
    ]"
    data-test="stash-logo"
    fill="currentColor"
    v-bind="computedLogoAttrs"
  />
</template>
